import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from '@/components/ui/card';
import { API } from '@/App';
import axios from 'axios';
import { Users, Building2, Briefcase, FileText, TrendingUp, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: 'Admin Dashboard | Talent Collective',
    description: 'Manage talent, projects, and applications on Talent Collective.'
  });

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = metrics ? [
    { 
      label: 'Total Talent', 
      value: metrics.total_talent, 
      icon: Users, 
      color: 'bg-indigo-500',
      link: '/admin/talent'
    },
    { 
      label: 'Total Companies', 
      value: metrics.total_companies, 
      icon: Building2, 
      color: 'bg-orange-500',
      link: '/admin/companies'
    },
    { 
      label: 'Active Projects', 
      value: metrics.active_projects, 
      icon: Briefcase, 
      color: 'bg-emerald-500',
      link: '/admin/projects'
    },
    { 
      label: 'Total Applications', 
      value: metrics.total_applications, 
      icon: FileText, 
      color: 'bg-purple-500',
      link: '/admin/applications'
    },
  ] : [];

  const pendingItems = metrics ? [
    { label: 'Pending Talent', value: metrics.pending_talent, link: '/admin/talent' },
    { label: 'Pending Companies', value: metrics.pending_companies, link: '/admin/companies' },
    { label: 'Pending Projects', value: metrics.pending_projects, link: '/admin/projects' },
  ] : [];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="heading-2 mb-2">Admin Dashboard</h1>
          <p className="text-slate-600">Overview of Talent Collective platform</p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-10 bg-slate-200 rounded mb-4"></div>
                <div className="h-8 bg-slate-200 rounded w-1/2"></div>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              {stats.map((stat, i) => (
                <Link to={stat.link} key={i}>
                  <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`h-12 w-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                    </div>
                    <p className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Pending Approvals & Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pending Approvals */}
              <Card className="p-6" data-testid="pending-approvals">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="heading-4">Pending Approvals</h2>
                    <p className="text-sm text-slate-500">Items requiring review</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {pendingItems.map((item, i) => (
                    <Link 
                      to={item.link} 
                      key={i}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <span className="text-slate-700">{item.label}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        item.value > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {item.value}
                      </span>
                    </Link>
                  ))}
                </div>
              </Card>

              {/* Quick Stats */}
              <Card className="p-6" data-testid="quick-stats">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="heading-4">Platform Health</h2>
                    <p className="text-sm text-slate-500">Key metrics</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <span className="text-slate-700">Approved Talent</span>
                    <span className="font-medium text-slate-900">{metrics?.approved_talent || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <span className="text-slate-700">Approved Companies</span>
                    <span className="font-medium text-slate-900">{metrics?.approved_companies || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <span className="text-slate-700">Applications This Week</span>
                    <span className="font-medium text-slate-900">{metrics?.applications_this_week || 0}</span>
                  </div>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
